export const CHECKOUT_URL = 'https://biodecision-en.impultienda.ar/checkout/'

export const beforeItems = [
  'You know the clinical objective, but you’re not sure how to structure the biomechanics.',
  'You spend too much time going back through textbooks, notes, and lectures.',
  'You find yourself thinking about the appliance before fully defining the force system.',
  'You aren’t always sure which reaction or side effect you should be monitoring.',
  'You know the theory, but applying it to a different clinical situation can still feel difficult.',
  'Every complex case seems to require starting your reasoning from scratch.',
]

export const afterItems = [
  'You start by defining the movement you want to achieve.',
  'You structure the force system before thinking about activation.',
  'You identify the reactions you need to consider.',
  'You evaluate the role of anchorage.',
  'You follow a visual decision pathway.',
  'You arrive at the activation stage with your reasoning already organized.',
]

export const painPoints = [
  {
    emoji: '😰',
    text: 'You have a complex case and know what you want to achieve, but you’re not completely sure how to organize the biomechanical analysis.',
  },
  {
    emoji: '⏱️',
    text: 'You spend too much time reviewing textbooks, notes, or courses before deciding which variables you need to consider.',
  },
  {
    emoji: '🤷',
    text: 'You know which appliance you could potentially use, but you’re not completely clear on the force system you’re actually trying to create.',
  },
  {
    emoji: '🧠',
    text: 'You understand the theory, but when a case doesn’t fit the textbook example, translating it into a practical sequence of decisions becomes difficult.',
  },
  {
    emoji: '⚠️',
    text: 'You want to think through the possible reactions and unwanted effects before activating a mechanic.',
  },
  {
    emoji: '😓',
    text: 'Before a complex mechanic, you sometimes feel like you need to review the entire topic again just to feel prepared.',
  },
]

export type Volume = {
  number: string
  title: string
  intro: string
  highlight?: string
  bullets?: string[]
  closing?: string
}

export const volumes: Volume[] = [
  {
    number: 'VOLUME I',
    title: 'THE BIOMECHANICS MASTER MAP',
    intro: 'Learn the core sequence for structuring a biomechanical analysis:',
    highlight: 'Objective → Movement → Force → Reaction → Anchorage → Activation → Control',
    closing:
      'A visual framework that helps you identify the key questions to consider before selecting or activating a mechanic.',
  },
  {
    number: 'VOLUME II',
    title: 'STOP STARTING WITH THE APPLIANCE',
    intro:
      'Learn how to begin with the movement you want to achieve, rather than immediately thinking about which appliance you have available.',
    closing:
      'Transform a clinical objective into biomechanical variables you can analyze before intervening.',
  },
  {
    number: 'VOLUME III',
    title: 'DESIGN THE FORCE SYSTEM BEFORE YOU ACTIVATE',
    intro:
      'Organize forces, moments, reactions, and anchorage considerations across different clinical situations.',
    closing:
      'Includes visual maps and matrices designed to help you structure your biomechanical reasoning.',
  },
  {
    number: 'VOLUME IV',
    title: 'FIND YOUR PATH FOR COMPLEX MECHANICS',
    intro: 'Visual decision maps for organizing the analysis of situations involving:',
    bullets: [
      'Molar uprighting',
      'Intrusion',
      'Extrusion',
      'Rotation',
      'Derotation',
      'Root control',
      'Molar movement',
      'Asymmetric mechanics',
      'Anchorage control',
    ],
    closing:
      'A collection of visual pathways designed to help you avoid starting from scratch with every complex case.',
  },
  {
    number: 'VOLUME V',
    title: 'IDENTIFY THE REACTIONS BEFORE THEY SURPRISE YOU',
    intro: 'Learn which variables deserve attention once you’ve defined a mechanic. Includes visual maps for:',
    bullets: [
      'Force reactions',
      'Unwanted effects',
      'Anchorage considerations',
      'Planning errors',
      'Monitoring variables',
      'Control points',
    ],
  },
  {
    number: 'VOLUME VI',
    title: 'THE CLINICAL DECISION PROTOCOL',
    intro: 'Bring the entire system together into one practical planning sequence. Work through a case using:',
    highlight:
      'PROBLEM → OBJECTIVE → MOVEMENT → FORCE SYSTEM → ANCHORAGE → ACTIVATION → CONTROL',
    closing: 'Includes worked clinical scenarios designed to help you practice the framework.',
  },
]

export const tools = [
  {
    emoji: '🧭',
    title: '30+ BIOMECHANICS DECISION MAPS',
    text: 'Visual pathways organized around different biomechanical situations and clinical objectives.',
  },
  {
    emoji: '📋',
    title: 'PRE-ACTIVATION CHECKLISTS',
    text: 'Quick-reference checklists to review before planning and activating a mechanic.',
  },
  {
    emoji: '⚙️',
    title: 'FORCE & MOVEMENT MATRICES',
    text: 'Visual tools for connecting clinical objectives with the biomechanical variables you need to consider.',
  },
  {
    emoji: '🌳',
    title: 'DECISION TREES',
    text: 'Structured pathways that organize the questions to consider before moving to the next stage of your analysis.',
  },
  {
    emoji: '📝',
    title: 'CASE PLANNING SHEETS',
    text: 'Professional worksheets for documenting the biomechanical reasoning behind a case.',
  },
  {
    emoji: '📊',
    title: 'CONTROL & REVIEW PROTOCOLS',
    text: 'Practical tools for identifying the variables that should be monitored after a mechanic is planned.',
  },
]

export type Bonus = {
  label: string
  title: string
  value: string
  image: string
  intro: string
  bullets?: string[]
  closing?: string
}

export const bonuses: Bonus[] = [
  {
    label: 'BONUS #1',
    title: '50 BIOMECHANICS DECISION CASES™',
    value: '$47',
    image: 'https://cdn.magicpatterns.com/uploads/9D2RvTP1AExAQnjhs3FMdh/BONUS_1_BIODECISION-EN.png',
    intro: '50 clinical scenarios designed to challenge your biomechanical reasoning.',
    closing:
      'Analyze the objective, identify the variables, follow the decision pathway, and then compare your reasoning with the worked analysis. Don’t just study biomechanics — practice thinking through it.',
  },
  {
    label: 'BONUS #2',
    title: 'PRE-ACTIVATION CHECKLIST™',
    value: '$37',
    image: 'https://cdn.magicpatterns.com/uploads/urCL6f48kqU1gQA84gb8wh/BONUS_2_BIODECISION-EN.png',
    intro: 'A rapid review tool to use before activating a mechanic. Review:',
    bullets: [
      'Clinical objective',
      'Desired movement',
      'Force system',
      'Expected reactions',
      'Anchorage',
      'Activation',
      'Control',
    ],
    closing: 'A final structured review before moving from analysis to activation.',
  },
  {
    label: 'BONUS #3',
    title: 'BIOMECHANICAL ERROR ATLAS™',
    value: '$47',
    image: 'https://cdn.magicpatterns.com/uploads/41djysdpoQQghRXw9tJ4xa/BONUS_3_BIODECISION-EN.png',
    intro:
      'A visual collection of common planning errors and the variables worth reviewing when a mechanic doesn’t behave as expected.',
    closing: 'Organized as: Situation → Variable to Review → What to Check → Control Point',
  },
  {
    label: 'BONUS #4',
    title: 'FORCE SYSTEM BLUEPRINT™',
    value: '$37',
    image: 'https://cdn.magicpatterns.com/uploads/7w9xGXoJ6TxdraPGSc3gU8/BONUS_4_BIODECISION-EN.png',
    intro: 'Visual planning templates for mapping the force system of a case before activation.',
    closing: 'Organize: Objective → Force → Moment → Reaction → Anchorage → Control',
  },
  {
    label: 'BONUS #5',
    title: 'ORTHO QUICK REFERENCE CARDS™',
    value: '$29',
    image: 'https://cdn.magicpatterns.com/uploads/iJJuPQ7DAXvR5DsGCEJf3y/BONUS_5_BIODECISION-EN.png',
    intro:
      'Quick-reference cards featuring essential biomechanical questions, matrices, concepts, and reminders.',
    closing: 'Designed for fast consultation from your computer, tablet, or phone.',
  },
]

export const testimonials = [
  {
    quote: 'A much easier way to organize my thinking.',
    body:
      'What I liked most is that it doesn’t feel like another long theory manual. It gives me a structure for what I should analyze before thinking about activation.',
    name: 'Maria P.',
    country: 'Colombia',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/f75a975d-e595-4c6f-ad1a-5c6b054546c0.jpg',
  },
  {
    quote: 'It changed the way I approach complex cases.',
    body: 'The Master Map helped me understand which questions I should be asking before selecting a mechanic.',
    name: 'Jorge R.',
    country: 'Mexico',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/2f746b6f-0963-4786-894c-5b3a67039017.jpg',
  },
  {
    quote: 'The cases make you actually think.',
    body: 'I like that you have to make your decision first and then compare it with the worked analysis.',
    name: 'Andrea P.',
    country: 'Venezuela',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/ac51cbe0-66ef-49ad-9b38-ce4e2ad9f4ea.jpg',
  },
  {
    quote: 'The planning sheets are extremely practical.',
    body: 'I use the planning framework when I need to structure a more complex mechanic.',
    name: 'Carlos S.',
    country: 'Spain',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/fac04728-9b99-4f1b-97f4-37befd286f11.jpg',
  },
  {
    quote: 'It feels like a system, not another PDF.',
    body: 'I wasn’t looking for another biomechanics book. I wanted something that could help me organize what I already know.',
    name: 'Lucía M.',
    country: 'Argentina',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/0a0f60f3-4800-4f33-902b-943103715ab7.jpg',
  },
]

export const faqs = [
  {
    q: 'How will I receive the material?',
    a: 'Immediately after your purchase, you’ll receive an email with instructions to access and download the materials. The six volumes and bonuses are delivered digitally in PDF format.',
  },
  {
    q: 'Is this suitable for students and practicing orthodontists?',
    a: 'Yes. The system is designed as a study and reference tool for advanced orthodontic students, residents, orthodontists in training, and clinicians who work with more complex mechanics.',
  },
  {
    q: 'Do I need to be a specialist?',
    a: 'The material assumes a foundational understanding of orthodontics and biomechanics. It is intended as an educational and planning resource and does not replace formal clinical education, professional judgment, supervision, or patient-specific treatment protocols.',
  },
  {
    q: 'Can I download the files?',
    a: 'Yes. All included materials are provided digitally and can be downloaded for personal use.',
  },
  {
    q: 'Can I access it from my phone?',
    a: 'Yes. You can access the materials from your computer, tablet, or smartphone.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. You pay $27 once and receive lifetime access to the materials included in this offer.',
  },
  {
    q: 'What exactly is included?',
    a: 'Your purchase includes: 6 Premium Volumes of Mechanics Decision Map™, 50 Biomechanics Decision Cases™, Pre-Activation Checklist™, Biomechanical Error Atlas™, Force System Blueprint™, and Ortho Quick Reference Cards™.',
  },
  {
    q: 'Is this a replacement for a biomechanics course?',
    a: 'No. Mechanics Decision Map™ is designed as a decision-structuring and clinical reference system, not as a replacement for formal education, hands-on training, clinical supervision, or professional judgment.',
  },
  {
    q: 'What if I decide it’s not for me?',
    a: 'Your purchase is protected by a 7-day guarantee. You can review the materials during the guarantee period and request a refund according to the terms of the guarantee.',
  },
]

export const purchaseNotifications = [
  { name: 'Valentina G.', country: 'Colombia' },
  { name: 'Diego M.', country: 'Mexico' },
  { name: 'Sofía R.', country: 'Argentina' },
  { name: 'Andrés L.', country: 'Peru' },
  { name: 'Camila T.', country: 'Chile' },
  { name: 'Javier N.', country: 'Spain' },
  { name: 'Mariana F.', country: 'Venezuela' },
  { name: 'Ricardo P.', country: 'Ecuador' },
  { name: 'Lucía B.', country: 'Uruguay' },
  { name: 'Fernando C.', country: 'Costa Rica' },
  { name: 'Gabriela S.', country: 'Guatemala' },
  { name: 'Héctor V.', country: 'Dominican Republic' },
  { name: 'Paula A.', country: 'Bolivia' },
  { name: 'Tomás E.', country: 'Paraguay' },
  { name: 'Isabel Q.', country: 'Panama' },
]
